namespace Portico.Tfs {
    import PortalGroup = Portico.Models.Setup.PortalGroup;
    import Inject = Portico.Decorators.Inject;
    import Service = Portico.Decorators.Service;

    export interface ITfsSettings {
        apiVersion: string;
        url: string;
        vsrm: string;
        project: string;
        token: string;
        porticoReleaseDefinitionId: string;
        porticoBuildDefinitionId: string;
    }

    export function tfsRequest(
        request: angular.IRequestConfig,
        settings: ITfsSettings,
        apiVersion: string
    ) {
        request.headers = request.headers || {};
        request.headers['Accept'] = `application/json;api-version=${apiVersion ||
            settings.apiVersion}`;
        request.headers['Authorization'] = `Basic ${settings.token}`;
        // request.withCredentials = true;

        request.url = _.trim(`${settings.url}/${_.trim(request.url, '/')}`, '/').replace('/?', '?');
        return request;
    }

    export function vsrmRequest(
        request: angular.IRequestConfig,
        settings: ITfsSettings,
        apiVersion: string
    ) {
        request.headers = request.headers || {};
        request.headers['Accept'] = `application/json;api-version=${apiVersion ||
            settings.apiVersion}`;
        request.headers['Authorization'] = `Basic ${settings.token}`;
        // request.withCredentials = true;

        request.url = _.trim(`${settings.vsrm}/${_.trim(request.url, '/')}`, '/').replace('/?', '?');
        return request;
    }

    @Service('app', 'tfsService')
    @Inject('$http', '$q', 'dataService', 'portalGroupService', 'setupService')
    export class TfsService {
        public readonly settings: ITfsSettings;
        private readonly templates: {
            build?: Portico.Tfs.Build.Definition.Get.Response;
            release?: Portico.Tfs.Release.Definition.Get.Response;
        } = {};

        constructor(
            private $http: angular.IHttpService,
            private $q: angular.IQService,
            private dataService: Portico.Services.IDataService,
            private portalGroupService: Portico.Services.PortalGroupService,
            private setupService: Portico.Services.SetupService
        ) {
            this.settings = {
                apiVersion: dataService.getDataValue('Reference', 'Id', 'tfs-api-version'),
                url: _.trim(dataService.getDataValue('Reference', 'Id', 'tfs-url'), '/'),
                vsrm: _.trim(dataService.getDataValue('Reference', 'Id', 'tfs-vsrm'), '/'),
                project: dataService.getDataValue('Reference', 'Id', 'tfs-project'),
                token: dataService.getDataValue('Reference', 'Id', 'tfs-token'),
                porticoReleaseDefinitionId: dataService.getDataValue(
                    'Reference',
                    'Id',
                    'tfs-portico-release-definition-id'
                ),
                porticoBuildDefinitionId: dataService.getDataValue(
                    'Reference',
                    'Id',
                    'tfs-portico-build-definition-id'
                ),
            };

            this.build = new TfsBuildService(this.$http, this.settings);
            this.buildDefinition = new TfsBuildDefinitionService(this.$http, this.settings);
            this.release = new TfsReleaseService(this.$http, this.settings);
            this.releaseDefinition = new TfsReleaseDefinitionService(this.$http, this.settings);
        }

        public build: TfsBuildService;
        public buildDefinition: TfsBuildDefinitionService;
        public release: TfsReleaseService;
        public releaseDefinition: TfsReleaseDefinitionService;

        private async getBuildDefinitionTemplate() {
            if (this.templates.build) return this.templates.build;

            const result = await this.buildDefinition.list({ name: 'Template' });
            if (result.count > 1 || result.count === 0) {
                throw new Error('error getting build definition template');
            }

            const definition = await this.buildDefinition.get(result.value[0].id);
            return (this.templates.build = definition);
        }

        private async getReleaseDefinitionTemplate() {
            if (this.templates.release) return this.templates.release;

            const result = (await this.releaseDefinition.list({ searchText: 'Ptl> Template' }));
            const template = _.find(result.value, x => x.name === 'Ptl> Template');
            if (!template) {
                throw new Error('error getting build definition template');
            }

            const definition = await this.releaseDefinition.get(result.value[0].id);
            return (this.templates.release = definition);
        }

        public async createOrUpdateBuildDefinition(
            portal: Portico.Models.Setup.Portal,
            portalGroup: Portico.Models.Setup.PortalGroup
        ) {
            const template = await this.getBuildDefinitionTemplate();
            const path = `\\Portals\\${portalGroup.name}`;
            const portalBuildDefinitionId = this.getPortalBuildDefinitionId(portal);
            const build = portalBuildDefinitionId
                ? await this.buildDefinition.get(portalBuildDefinitionId)
                : await this.buildDefinition.find(
                      {
                          name: portal.name,
                      },
                      x => x.path.toLowerCase() === path.toLowerCase()
                  );

            const creating = !build;
            const id = creating ? 0 : build.id;
            const comment = creating ? 'Created' : 'Updated';
            const revision = build ? build.revision : 0;

            const variables = _.cloneDeep(template.variables);
            variables['Portal.Id'].value = portal.id;

            const request:
                | Tfs.Build.Definition.Create.Request
                | Tfs.Build.Definition.Update.Request = {
                id,
                revision,
                name: portal.name,
                buildNumberFormat: template.buildNumberFormat,
                path,
                definitionType: 'build',
                documentQuality: 'definition',
                queue: {
                    id: template.queue.id,
                },
                build: _.cloneDeep(template.build),
                repository: _.cloneDeep(template.repository),
                options: _.cloneDeep(template.options),
                variables,
                retentionRules: _.cloneDeep(template.retentionRules),
                badgeEnabled: true,
                comment: `${comment} portal build definition for ${portalGroup.name} / ${
                    portal.name
                } (id: ${portal.id})`,
            };

            if (creating) {
                return await this.buildDefinition.create(request);
            }

            return await this.buildDefinition.update(build.id, request);
        }

        private getPortalReleaseDefinitionId(portal: Portico.Models.Setup.Portal) {
            return JSON.parse(portal.settings || '{}').releaseDefinitionId;
        }

        private getPortalBuildDefinitionId(portal: Portico.Models.Setup.Portal) {
            return JSON.parse(portal.settings || '{}').buildDefinitionId;
        }

        public async createOrUpdateReleaseDefinition(
            buildDefinition: Tfs.Build.Definition.Get.Response,
            portal: Portico.Models.Setup.Portal,
            portalGroup: Portico.Models.Setup.PortalGroup
        ) {
            const template = await this.getReleaseDefinitionTemplate();
            const name = `Ptl> ${portalGroup.name} > ${portal.name}`;
            const portalReleaseDefinitionId = this.getPortalReleaseDefinitionId(portal);
            const release = portalReleaseDefinitionId
                ? await this.releaseDefinition.get(portalReleaseDefinitionId)
                : await this.releaseDefinition.find(
                      {
                          searchText: name,
                      },
                      x => x.name.toLowerCase() === name.toLowerCase()
                  );

            const creating = !release;
            const id = creating ? 0 : release.id;
            const revision = release ? release.revision : 0;

            const variables = _.cloneDeep(template.variables);
            variables['Portal.Id'].value = portal.id;
            variables['Portal.Name'].value = portal.name;

            function cloneEnvironments() {
                const results: Portico.Tfs.Release.Definition.Get.Environment[] = [];
                for (let env of template.environments) {
                    results.push({
                        name: env.name,
                        rank: env.rank,
                        conditions: _.cloneDeep(env.conditions),
                        demands: _.cloneDeep(env.demands),
                        deployPhases: _.cloneDeep(env.deployPhases),
                        deployStep: {
                            tasks: _.cloneDeep(env.deployStep.tasks),
                        } as any,
                        environmentOptions: _.cloneDeep(env.environmentOptions),
                        executionPolicy: _.cloneDeep(env.executionPolicy),
                        postDeployApprovals: {
                            approvals: _.cloneDeep(env.postDeployApprovals.approvals),
                        },
                        preDeployApprovals: {
                            approvals: _.cloneDeep(env.preDeployApprovals.approvals),
                        },
                        queueId: buildDefinition.queue.id,
                        retentionPolicy: _.cloneDeep(env.retentionPolicy),
                        schedules: _.cloneDeep(env.schedules),
                        variables: _.cloneDeep(env.variables),
                    } as any);
                }
                return results;
            }

            const request: Tfs.Release.Definition.Create.Request = {
                id,
                revision,
                source: 2 as any,
                name,
                releaseNameFormat: template.releaseNameFormat,
                retentionPolicy: _.cloneDeep(template.retentionPolicy),
                variables,
                artifacts: [
                    ...template.artifacts.map(x => {
                        x.isPrimary = false;
                        return x;
                    }),
                    {
                        sourceId: `${buildDefinition.project.id}:${buildDefinition.id}`,
                        type: 'Build',
                        alias: 'portal',
                        isPrimary: true,
                        definitionReference: {
                            artifactSourceDefinitionUrl: {
                                id: `https://tfs.trialcard.com/_permalink/_build/index?collectionId=b573ff9b-5d96-4d57-81bc-f38d848e83b9&projectId=${
                                    buildDefinition.project.id
                                }&definitionId=${buildDefinition.id}`,
                                name: `${buildDefinition.name}`,
                            },
                            definition: {
                                id: `${buildDefinition.id}`,
                                name: `${buildDefinition.name}`,
                            },
                            project: {
                                id: `${buildDefinition.project.id}`,
                                name: `${buildDefinition.project.name}`,
                            },
                        },
                    },
                ],
                environments: cloneEnvironments(),
                triggers: [
                    {
                        artifactAlias: 'portal',
                        triggerConditions: [
                            {
                                sourceBranch: 'master',
                                tags: [],
                            },
                        ],
                        triggerType: 'artifactSource',
                    },
                ],
            } as any;

            if (creating) {
                return await this.releaseDefinition.create(request);
            }

            return await this.releaseDefinition.update(release.id, request);
        }

        public async syncPortal(portal: Portico.Models.Setup.Portal) {
            const portalGroup = _.find(
                await this.portalGroupService.get(),
                x => x.id === portal.portalGroupId
            );
            const buildDefinition = await this.createOrUpdateBuildDefinition(portal, portalGroup);
            const releaseDefinition = await this.createOrUpdateReleaseDefinition(
                buildDefinition,
                portal,
                portalGroup
            );

            const settings = JSON.parse(portal.settings || '{}');
            settings.buildDefinitionId = buildDefinition.id;
            settings.releaseDefinitionId = releaseDefinition.id;
            portal.settings = JSON.stringify(settings);

            await this.setupService.savePortal(portal);
        }

        porticoReleases: Release.List.Value[];
        // latestPorticoBuild: Build.Get.Response;

        public async getLatestPorticoInformation() {
            const possibleReleases = await this.release.list({
                $top: 1000,
                $expand: 'environments,artifacts',
                definitionId: this.settings.porticoReleaseDefinitionId as any,
            });
            this.porticoReleases = possibleReleases.value;
        }

        public viewPortal(portal: any) {
            portal.isOpen = !portal.isOpen;
        }

        private hasBuild(portal: Portico.Models.Setup.Portal) {
            return (
                this.getPortalBuildDefinitionId(portal) && this.getPortalReleaseDefinitionId(portal)
            );
        }
    }
}
